import { useEffect, useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { apiClient } from '@/lib/api-client'
import { toast } from 'react-hot-toast'

interface SoftwareFormProps {
  isOpen: boolean
  softwareId: number | null
  onClose: () => void
  onSuccess: () => void
}

interface FormData {
  name: string
  vendor: string
  website: string
  description: string
  accountName: string
  email: string
  plan: string
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1.5 text-sm">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  )
}

export function SoftwareForm({ isOpen, softwareId, onClose, onSuccess }: SoftwareFormProps) {
  const [loading, setLoading] = useState(false)
  const { register, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      name: '',
      vendor: '',
      website: '',
      description: '',
      accountName: '',
      email: '',
      plan: '',
    },
  })

  useEffect(() => {
    if (!isOpen) return
    if (!softwareId) {
      reset({
        name: '', vendor: '', website: '', description: '', accountName: '', email: '', plan: '',
      })
      return
    }
    let cancelled = false
    apiClient.get(`/software/${softwareId}`).then((response) => {
      if (cancelled) return
      const item = response.data
      reset({
        name: item.name || '',
        vendor: item.vendor || '',
        website: item.website || '',
        description: item.description || '',
        accountName: '',
        email: '',
        plan: '',
      })
    }).catch(() => toast.error('Could not load this software'))
    return () => {
      cancelled = true
    }
  }, [isOpen, softwareId, reset])

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    try {
      if (softwareId) {
        await apiClient.put(`/software/${softwareId}`, {
          name: data.name,
          vendor: data.vendor,
          website: data.website,
          description: data.description,
        })
        toast.success('Software updated')
      } else {
        await apiClient.post('/software', {
          name: data.name,
          vendor: data.vendor,
          website: data.website,
          description: data.description,
          account: data.email.trim()
            ? { accountName: data.accountName || data.email, email: data.email, plan: data.plan }
            : undefined,
        })
        toast.success('Software added')
      }
      onSuccess()
      onClose()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Could not save software')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={softwareId ? 'Edit software' : 'Add software'} size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Software name">
            <Input {...register('name', { required: true, minLength: 2 })} placeholder="Figma" />
          </Field>
          <Field label="Vendor">
            <Input {...register('vendor')} placeholder="Figma Inc." />
          </Field>
        </div>
        <Field label="Website">
          <Input {...register('website')} placeholder="https://www.figma.com" />
        </Field>
        <Field label="Description">
          <textarea
            {...register('description')}
            rows={3}
            placeholder="What this software is used for"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </Field>
        {!softwareId && (
          <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
            <p className="text-sm font-medium">First paid account</p>
            <p className="text-xs text-muted-foreground">Optional. You can add more accounts after the software is created.</p>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Account name">
                <Input {...register('accountName')} placeholder="Design team" />
              </Field>
              <Field label="Account email">
                <Input {...register('email')} placeholder="design@simplogics.com" />
              </Field>
              <Field label="Plan">
                <Input {...register('plan')} placeholder="Organization" />
              </Field>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={loading}>
            {loading && <LoadingSpinner size="sm" className="mr-2" />}
            {softwareId ? 'Save software' : 'Add software'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
